/**
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { of as observableOf } from 'rxjs';
import { filter, delay, takeWhile } from 'rxjs/operators';

import { ElSearchService } from './search.service';
import { ElThemeService } from '../../services/theme.service';
import { ElOverlayService } from '../cdk/overlay/overlay-service';
import { ElOverlayRef, ElPortalDirective } from '../cdk/overlay/mapping';

/**
 * search-field-component is used under the hood by el-search component
 * can't be used itself
 */
@Component({
    selector: 'el-search-field',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [
        'styles/search.component.modal-zoomin.css',
        'styles/search.component.layout-rotate.css',
        'styles/search.component.modal-move.css',
        'styles/search.component.curtain.css',
        'styles/search.component.column-curtain.css',
        'styles/search.component.modal-drop.css',
        'styles/search.component.modal-half.css',
    ],
    template: `
    <div class="search" (keyup.esc)="emitClose()">
      <button (click)="emitClose()" elButton ghost class="close-button">
        <el-icon icon="close-outline" pack="endlessjs-essentials"></el-icon>
      </button>
      <div class="form-wrapper">
        <form class="form" (keyup.enter)="submitSearch(searchInput.value)">
          <div class="form-content">
            <input class="search-input"
                   #searchInput
                   (input)="emitSearchInput(searchInput.value)"
                   autocomplete="off"
                   [attr.placeholder]="placeholder"
                   tabindex="-1"
                   (blur)="focusInput()"/>
          </div>
          <span class="info">{{ hint }}</span>
        </form>
      </div>
    </div>
  `,
})
export class ElSearchFieldComponent implements OnChanges, AfterViewInit {

    static readonly TYPE_MODAL_ZOOMIN = 'modal-zoomin';
    static readonly TYPE_ROTATE_LAYOUT = 'rotate-layout';
    static readonly TYPE_MODAL_MOVE = 'modal-move';
    static readonly TYPE_CURTAIN = 'curtain';
    static readonly TYPE_COLUMN_CURTAIN = 'column-curtain';
    static readonly TYPE_MODAL_DROP = 'modal-drop';
    static readonly TYPE_MODAL_HALF = 'modal-half';

    @Input() type: string;
    @Input() placeholder: string;
    @Input() hint: string;
    @Input() show = false;

    @Output() close = new EventEmitter();
    @Output() search = new EventEmitter();
    @Output() searchInput = new EventEmitter();

    @ViewChild('searchInput', { static: false }) inputElement: ElementRef<HTMLInputElement>;

    @HostBinding('class.show')
    get showClass() {
        return this.show;
    }

    @HostBinding('class.modal-zoomin')
    get modalZoomin() {
        return this.type === ElSearchFieldComponent.TYPE_MODAL_ZOOMIN;
    }

    @HostBinding('class.rotate-layout')
    get rotateLayout() {
        return this.type === ElSearchFieldComponent.TYPE_ROTATE_LAYOUT;
    }

    @HostBinding('class.modal-move')
    get modalMove() {
        return this.type === ElSearchFieldComponent.TYPE_MODAL_MOVE;
    }

    @HostBinding('class.curtain')
    get curtain() {
        return this.type === ElSearchFieldComponent.TYPE_CURTAIN;
    }

    @HostBinding('class.column-curtain')
    get columnCurtain() {
        return this.type === ElSearchFieldComponent.TYPE_COLUMN_CURTAIN;
    }

    @HostBinding('class.modal-drop')
    get modalDrop() {
        return this.type === ElSearchFieldComponent.TYPE_MODAL_DROP;
    }

    @HostBinding('class.modal-half')
    get modalHalf() {
        return this.type === ElSearchFieldComponent.TYPE_MODAL_HALF;
    }

    ngOnChanges({ show }: SimpleChanges) {
        const becameHidden = !show.isFirstChange() && show.currentValue === false;
        if (becameHidden && this.inputElement) {
            this.inputElement.nativeElement.value = '';
        }

        this.focusInput();
    }

    ngAfterViewInit() {
        this.focusInput();
    }

    emitClose() {
        this.close.emit();
    }

    submitSearch(term) {
        if (term) {
            this.search.emit(term);
        }
    }

    emitSearchInput(term: string) {
        this.searchInput.emit(term);
    }

    focusInput() {
        if (this.show && this.inputElement) {
            this.inputElement.nativeElement.focus();
        }
    }
}

export type ElSearchType = 'modal-zoomin' | 'rotate-layout' | 'modal-move' |
    'curtain' | 'column-curtain' | 'modal-drop' | 'modal-half';

/**
 * Beautiful full-page search control.
 *
 * @stacked-example(Showcase, search/search-showcase.component)
 *
 * Basic setup:
 *
 * ```ts
 *  <el-search type="rotate-layout"></el-search>
 * ```
 * ### Installation
 *
 * Import `ElSearchModule` to your feature module.
 * ```ts
 * @NgModule({
 *   imports: [
 *     // ...
 *     ElSearchModule,
 *   ],
 * })
 * export class PageModule { }
 * ```
 * ### Usage
 *
 * Several animation types are available:
 * modal-zoomin, rotate-layout, modal-move, curtain, column-curtain, modal-drop, modal-half
 *
 * It is also possible to handle search event using `ElSearchService`:
 *
 * @stacked-example(Search Event, search/search-event.component)
 *
 * @styles
 *
 * search-background-color:
 * search-divider-color:
 * search-divider-style:
 * search-divider-width:
 * search-extra-background-color:
 * search-text-color:
 * search-text-font-family:
 * search-text-font-size:
 * search-text-font-weight:
 * search-text-line-height:
 * search-placeholder-text-color:
 * search-info-text-color:
 * search-info-text-font-family:
 * search-info-text-font-size:
 * search-info-text-font-weight:
 * search-info-text-line-height:
 */
@Component({
    selector: 'el-search',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['styles/search.component.css'],
    template: `
    <button #searchButton class="start-search" (click)="emitActivate()" elButton ghost>
      <el-icon icon="search-outline" pack="endlessjs-essentials"></el-icon>
    </button>
    <el-search-field
      *elPortal
      [show]="showSearchField"
      [type]="type"
      [placeholder]="placeholder"
      [hint]="hint"
      (search)="search($event)"
      (searchInput)="emitInput($event)"
      (close)="emitDeactivate()">
    </el-search-field>
  `,
})
export class ElSearchComponent implements OnInit, OnDestroy {

    private alive = true;
    private overlayRef: ElOverlayRef;
    showSearchField = false;

    /**
     * Tags a search with some ID, can be later used in the search service
     * to determine which search component triggered the action, if multiple searches exist on the page.
     *
     * @type {string}
     */
    @Input() tag: string;

    /**
     * Search input placeholder
     * @type {string}
     */
    @Input() placeholder: string = 'Search...';

    /**
     * Hint showing under the input field to improve user experience
     *
     * @type {string}
     */
    @Input() hint: string = 'Hit enter to search';

    /**
     * Search design type, available types are
     * modal-zoomin, rotate-layout, modal-move, curtain, column-curtain, modal-drop, modal-half
     * @type {string}
     */
    @Input() type: ElSearchType;

    @ViewChild(ElPortalDirective, { static: false }) searchFieldPortal: ElPortalDirective;
    @ViewChild('searchButton', { read: ElementRef, static: false }) searchButton: ElementRef<HTMLElement>;

    constructor(
        private searchService: ElSearchService,
        private themeService: ElThemeService,
        private router: Router,
        private overlayService: ElOverlayService,
        private changeDetector: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        this.router.events
            .pipe(
                takeWhile(() => this.alive),
                filter(event => event instanceof NavigationEnd),
            )
            .subscribe(() => this.hideSearch());

        this.searchService.onSearchActivate()
            .pipe(
                takeWhile(() => this.alive),
                filter(data => !this.tag || data.tag === this.tag),
            )
            .subscribe(() => this.openSearch());

        this.searchService.onSearchDeactivate()
            .pipe(
                takeWhile(() => this.alive),
                filter(data => !this.tag || data.tag === this.tag),
            )
            .subscribe(() => this.hideSearch());
    }

    ngOnDestroy() {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.removeLayoutClasses();
            this.overlayRef.detach();
        }

        this.alive = false;
    }

    openSearch() {
        if (!this.overlayRef) {
            this.overlayRef = this.overlayService.create();
            this.overlayRef.attach(this.searchFieldPortal);
        }

        this.themeService.appendLayoutClass(this.type);
        observableOf(null).pipe(delay(0)).subscribe(() => {
            this.themeService.appendLayoutClass('with-search');
            this.showSearchField = true;
            this.changeDetector.detectChanges();
        });
    }

    hideSearch() {
        this.removeLayoutClasses();
        this.showSearchField = false;
        this.changeDetector.detectChanges();
        this.searchButton.nativeElement.focus();
    }

    search(term) {
        this.searchService.submitSearch(term, this.tag);
        this.hideSearch();
    }

    emitInput(term: string) {
        this.searchService.searchInput(term, this.tag);
    }

    emitActivate() {
        this.searchService.activateSearch(this.type, this.tag);
    }

    emitDeactivate() {
        this.searchService.deactivateSearch(this.type, this.tag);
    }

    private removeLayoutClasses() {
        this.themeService.removeLayoutClass('with-search');
        observableOf(null).pipe(delay(500)).subscribe(() => {
            this.themeService.removeLayoutClass(this.type);
        });
    }
}
